import { useMemo, useState } from "react";
import { useDebounce } from "../hooks/use-debounce";
import { UseProvidersListProps } from "../queries/provider/setting/use-providers-list";
import { useProviderListV2 } from "../queries/use-provider-list-v2";
import { formatDate } from "../utils/date-time-utility";
import { DEF_PAGINATION, DGT_Pagination, getXtenantId } from "../utils/utils";
import { AsyncSelectOption } from "./AsyncSelect";
import AsyncSelectScroll from "./AsyncSelectScroll";
import Flex from "./Flex";
import Text from "./Text";

interface ProviderSelectScrollProps {
  value: AsyncSelectOption | null;
  onChange: (value: AsyncSelectOption | null) => void;
  label?: string;
  required?: boolean;
  error?: string | boolean;
  providerSearchParam?: UseProvidersListProps;
  name?: string;
  handleBlur?: (e: any) => void;
  disabled?: boolean;
  defaultSearchKey?: string;
  prefillListOption?: AsyncSelectOption;
  viewBirthDate?: boolean;
  isMatchButtonHeight?: boolean;
}

/**
 * Infinite-scroll variant of {@link ProviderSelect}: same provider search, but
 * the dropdown loads more providers on scroll instead of only the first page.
 *
 * @author [Rajkumar Rathod]
 */
const ProviderSelectScroll = ({
  value,
  onChange,
  label,
  required,
  error,
  providerSearchParam,
  name,
  handleBlur,
  disabled,
  defaultSearchKey,
  prefillListOption,
  viewBirthDate = true,
  isMatchButtonHeight,
}: ProviderSelectScrollProps) => {
  const [searchKey, setSearchKey] = useDebounce<string>(defaultSearchKey || "");
  const [pagination, setPagination] = useState<DGT_Pagination>(
    providerSearchParam?.pagination ?? DEF_PAGINATION
  );

  const {
    data: providersList,
    isLoading,
    isFetching,
  } = useProviderListV2({
    status: providerSearchParam?.status ?? true,
    xTENANTID: providerSearchParam?.xTENANTID ?? getXtenantId(),
    ...providerSearchParam,
    pagination,
    searchString: searchKey,
  });

  const handleSearch = (v: string) => {
    setSearchKey(v);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const options: AsyncSelectOption[] = useMemo(() => {
    const list =
      providersList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.name,
        birthDate: it?.birthDate ?? "",
        colorCode: it?.colorCode ?? "",
      })) ?? [];

    if (
      prefillListOption &&
      !list.some((opt) => opt.value === prefillListOption.value)
    ) {
      return [prefillListOption, ...list];
    }

    return list;
  }, [providersList?.data?.content, prefillListOption]);

  return (
    <AsyncSelectScroll
      name={name}
      isMatchButtonHeight={isMatchButtonHeight}
      handleBlur={handleBlur}
      required={required}
      error={error}
      loading={isLoading || isFetching}
      renderCustomLabel={(option) => (
        <Flex gap={1} alignItems={"center"} justifyContent={"row"}>
          <Flex flexDirection={"column"}>
            <Text>{option.label}</Text>
            {viewBirthDate && (
              <Text>
                {option?.birthDate ? formatDate(option?.birthDate) : ""}
              </Text>
            )}
          </Flex>
        </Flex>
      )}
      options={options}
      label={label}
      placeholder="Search Provider"
      onSearch={handleSearch}
      value={value}
      onChange={onChange}
      disabled={disabled}
      pagination={pagination}
      setPagination={setPagination}
      totalElements={providersList?.data?.page?.totalElements}
    />
  );
};

export default ProviderSelectScroll;
